import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Card,
  Typography,
  Button,
  Chip,
  Box,
  useTheme,
  useMediaQuery,
  Tooltip
} from '@mui/material';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import ImageIcon from '@mui/icons-material/Image';
import DescriptionIcon from '@mui/icons-material/Description';
import InfoIcon from '@mui/icons-material/Info';
import DeleteIcon from '@mui/icons-material/Delete';
import { deleteTaskFiles } from '../../store/actions/tasksActions';
import { tasksAPI } from '../../services/api';
import GetAppIcon from '@mui/icons-material/GetApp';
import { showNotification } from '../../store/actions/uiActions';
import { safeDecodeFileName, getFileNameWithoutExtension } from '../../utils/fileNameUtils';
import DeleteConfirmDialog from './DeleteConfirmDialog';

const TaskCard = ({ task }) => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  //const [setOpenConfirm] = React.useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = React.useState(false);
  const { loading } = useSelector(state => state.tasks);
  const statuses = useSelector(state => state.ui.statuses || []);
  
  // 格式化创建和完成时间
  /*const createdDate = task.created_at 
    ? format(new Date(task.created_at), 'yyyy-MM-dd HH:mm') 
    : '--';
  const formattedDate = task.completed_at 
    ? format(new Date(task.completed_at), 'yyyy-MM-dd HH:mm') 
    : '--';*/
  
  // 获取显示的文件名（解码后的）
  const displayFileName = safeDecodeFileName(task.file_name);
  
  // 语言代码到中文名称的映射
  const languageMap = {
    'en': '英语',
    'ja': '日语',
    'zh': '中文',
    'ko': '韩语',
    'fr': '法语',
    'de': '德语',
    'es': '西班牙语',
    'it': '意大利语',
    'ru': '俄语',
    'pt': '葡萄牙语',
    'ar': '阿拉伯语',
    'hi': '印地语',
    'th': '泰语',
    'vi': '越南语'
    // 可以根据需要添加更多语言
  };
  
  // 获取语言的中文名称
  const getLanguageName = (code) => {
    return languageMap[code] || code; // 如果没有映射则显示原始代码
  };
  
  // 认证类型映射
  const certificationTypeMap = {
    'none': 'AI翻译',
    'professional': '专业认证服务',
    'naati': 'NAATI认证服务'
  };
  
  // 获取认证类型的中文名称
  const getCertificationTypeName = (type) => {
    return certificationTypeMap[type] || 'AI翻译'; // 默认显示AI翻译
  };
  
  // 获取状态的中文名称 - 从Redux store中的statuses数组获取
  const getStatusName = (statusCode) => {
    const statusObj = statuses.find(s => s.code === statusCode);
    return statusObj ? statusObj.name : statusCode;
  };
  
  // 获取文件图标
  const getFileIcon = () => {
    switch(task.file_format) {
      case 'PDF':
        return <PictureAsPdfIcon />;
      case 'JPG':
      case 'JPEG':
      case 'PNG':
        return <ImageIcon />;
      case 'DOC':
      case 'DOCX':
        return <DescriptionIcon />;
      default:
        return <DescriptionIcon />;
    }
  };

  // 检查文件是否已过期
  const isFileExpired = () => {
    if (!task.file_expiry) return true;
    
    // 如果 is_expired 为 true，直接返回已过期
    if (task.file_expiry.is_expired) return true;
    
    // 如果有过期时间，检查是否已过当前时间
    if (task.file_expiry.file_expires_at) {
      const expiryTime = new Date(task.file_expiry.file_expires_at); // UTC时间
      const now = new Date(); // 当前本地时间
  
      return now > expiryTime; // 当前时间大于过期时间表示已过期
    }
    
    return false;
  };

  // 格式化剩余时间
  const formatRemainingTime = () => {
    // 如果文件已被清理，不显示剩余时间
    if (task.files_cleaned === 1) return '';
    // 如果没有过期信息或文件已过期，不显示剩余时间
    if (!task.file_expiry || isFileExpired()) return '';
    if(isFileExpired()) return '';
    // 如果没有过期时间，返回空
    if (!task.file_expiry.file_expires_at) return '';
    
    const expiryTime = new Date(task.file_expiry.file_expires_at); // UTC时间
    const now = new Date(); // 当前本地时间
    const remainingMs = expiryTime.getTime() - now.getTime();
    
    // 如果剩余时间小于等于0，说明已过期
    if (remainingMs <= 0) return '';
    
    // 计算剩余天数
    const remainingDays = Math.floor(remainingMs / (1000 * 60 * 60 * 24));
    if (remainingDays > 0) {
      return `${remainingDays}天后自动清理`;
    }
    
    // 计算剩余小时数
    const remainingHours = Math.floor(remainingMs / (1000 * 60 * 60));
    if (remainingHours > 0) {
      return `${remainingHours}小时后自动清理`;
    }
    
    // 计算剩余分钟数
    const remainingMinutes = Math.ceil(remainingMs / (1000 * 60));
    if (remainingMinutes > 0) {
      return `${remainingMinutes}分钟后自动清理`;
    }
    
    return '';
  };
  
  // 处理删除文件
  const handleDeleteFiles = () => {
    setDeleteDialogOpen(true);
  };

  // 确认删除文件
  const handleConfirmDelete = () => {
    dispatch(deleteTaskFiles(task.task_id));
    setDeleteDialogOpen(false);
  };

  // 取消删除
  const handleCancelDelete = () => {
    setDeleteDialogOpen(false);
  };

  // 下载文件函数
  const downloadFile = (taskId) => {
    const downloadUrl = tasksAPI.getDownloadUrl(taskId);
    const token = localStorage.getItem('token');
    
    /*console.log('开始下载文件:', {
      taskId,
      downloadUrl
    });*/
    
    // 使用Fetch API进行下载，以便能够设置授权请求头
    fetch(downloadUrl, {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    })
      .then(response => {
        if (!response.ok) {
          if (response.status === 410) {
            throw new Error('文件下载链接已过期');
          }
          throw new Error(`下载失败: ${response.status} ${response.statusText}`);
        }
        
        // 从响应头获取文件名
        const contentDisposition = response.headers.get('Content-Disposition');
        let fileName = '';
        
        //console.log('Content-Disposition 响应头:', contentDisposition);
        
        if (contentDisposition) {
          // 解析Content-Disposition头获取文件名
          // 支持RFC 5987标准格式: filename*=UTF-8''encoded-name
          const rfc5987Match = /filename\*=UTF-8''([^;]+)/.exec(contentDisposition);
          if (rfc5987Match) {
            fileName = decodeURIComponent(rfc5987Match[1]);
            //console.log('使用RFC 5987格式解析文件名:', fileName);
          } else {
            // 传统格式: filename="name" 或 filename=name
            const traditionalMatch = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/.exec(contentDisposition);
            if (traditionalMatch != null && traditionalMatch[1]) {
              fileName = decodeURIComponent(traditionalMatch[1].replace(/['"]/g, ''));
              //console.log('使用传统格式解析文件名:', fileName);
            }
          }
        }
        
        // 如果无法从响应头获取文件名，生成默认文件名
        if (!fileName) {
          const baseFileName = getFileNameWithoutExtension(displayFileName);
          const contentType = response.headers.get('Content-Type');
          // 根据Content-Type判断文件类型
          if (contentType === 'application/zip') {
            fileName = `${baseFileName}_翻译结果.zip`;
          } else {
            fileName = `${baseFileName}_翻译结果.pdf`;
          }
        }
        
        //console.log('解析的文件名:', fileName);
        
        return response.blob().then(blob => ({ blob, fileName }));
      })
      .then(({ blob, fileName }) => {
        // 创建一个Blob URL并用隐藏链接下载
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', fileName);
        document.body.appendChild(link);
        link.click();
        
        // 清理
        setTimeout(() => {
          document.body.removeChild(link);
          window.URL.revokeObjectURL(url);
        }, 100);
      })
      .catch(error => {
        //console.error('文件下载错误:', error);
        dispatch(showNotification(error.message || '下载失败', 'error'));
      });
  };
  
  // 打开取消确认对话框
  /*const handleOpenConfirm = () => {
    setOpenConfirm(true);
  };*/
  
  // 关闭取消确认对话框
  /*const handleCloseConfirm = () => {
    setOpenConfirm(false);
  };*/
  
  // 处理取消任务
  /*const handleCancelTask = async () => {
    try {
      await dispatch(cancelTask(task.task_id));
      handleCloseConfirm();
    } catch (error) {
      console.error('取消任务失败:', error);
    }
  };*/
  
  // 判断是否可以取消（仅待翻译状态可以取消，翻译中状态不能取消）
  //const canCancel = task.status === 'waiting' || task.status === '待翻译';
  
  // 判断是否可以下载（翻译完成且文件未过期且文件未被清理）
  const canDownload = 
    (task.status === 'completed' || task.status === '翻译完成') && 
    !isFileExpired() &&
    (task.files_cleaned === 0 || task.files_cleaned === undefined || task.files_cleaned === null);

  // 判断是否显示文件已清理提示（翻译完成但文件已过期，或者任务已取消，或者文件已被清理）
  const showFileCleanedTip = 
    ((task.status === 'completed' || task.status === '翻译完成') && 
    isFileExpired()) ||
    (task.status === 'cancelled' || task.status === '已取消')||
    (task.status === 'failed' || task.status === '翻译失败') ||
    (task.files_cleaned === 1);

  return (
    <>
      <Card 
        sx={{ 
          width: '100%',
          height: isMobile ? '80px' : '80px',
          transition: 'all 0.3s ease',
          '&:hover': {
            transform: 'translateY(-3px)',
            boxShadow: '0 8px 20px rgba(0,0,0,0.08)'
          },
          p: isMobile ? 0.4 : 0.8,
          position: 'relative',
          borderRadius: isMobile ? '40px' : '60px',
          display: 'flex',
          alignItems: 'center',
          boxShadow: '0 2px 10px rgba(0,0,0,0.04)',
          bgcolor: 'white',
          border: '1px solid',
          borderColor: 'rgba(230, 230, 230, 0.7)'
        }}
      >
        {/* 文件格式图标 - 左侧 */}
        <Tooltip 
          title={
            task.error_message && 
            ((task.status === 'failed' || task.status === '翻译失败') ||
             (task.status === 'cancelled' || task.status === '已取消'))
              ? task.error_message 
              : ""
          }
          arrow 
          placement="top"
          disableHoverListener={
            !(task.error_message && 
              ((task.status === 'failed' || task.status === '翻译失败') ||
               (task.status === 'cancelled' || task.status === '已取消')))
          }
        >
          <Box
            sx={{ 
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              color: (task.status === 'failed' || task.status === '翻译失败' ||
                     task.status === 'cancelled' || task.status === '已取消')
                ? theme.palette.error.main 
                : theme.palette.primary.main,
              width: isMobile ? 32 : 45,
              height: isMobile ? 32 : 45,
              ml: isMobile ? 1 : 2,
              borderRadius: '50%',
              bgcolor: (task.status === 'failed' || task.status === '翻译失败' ||
                       task.status === 'cancelled' || task.status === '已取消')
                ? 'rgba(244, 67, 54, 0.08)'  // 浅红色背景
                : 'rgba(67, 83, 255, 0.08)',
              transition: 'all 0.2s ease',
              cursor: task.error_message && 
                      ((task.status === 'failed' || task.status === '翻译失败') ||
                       (task.status === 'cancelled' || task.status === '已取消'))
                ? 'help' : 'default',
              '&:hover': {
                bgcolor: (task.status === 'failed' || task.status === '翻译失败' ||
                         task.status === 'cancelled' || task.status === '已取消')
                  ? 'rgba(244, 67, 54, 0.12)'  // hover时稍深的红色
                  : 'rgba(67, 83, 255, 0.12)',
              },
              '& svg': {
                fontSize: isMobile ? '1rem' : '1.5rem'
              }
            }}
          >
            {getFileIcon()}
          </Box>
        </Tooltip>
          
        {/* 文件名 - 不显示后缀 */}
        <Typography 
          variant="body2"
          component="div"
          fontWeight={600}
          noWrap
          title={displayFileName}
          fontSize={isMobile ? '0.9rem' : '1.1rem'}
          sx={{ 
            lineHeight: 1.2, 
            color: theme.palette.primary.main,
            width: isMobile ? '120px' : '180px',
            mx: isMobile ? 1 : 2,
            transition: 'color 0.2s',
            '&:hover': {
              color: theme.palette.primary.dark
            }
          }}
        >
          {getFileNameWithoutExtension(displayFileName)}
        </Typography>
            
        {/* 文件信息 */}
        <Box sx={{ 
           display: 'flex', 
           flexDirection: 'row',
           alignItems: isMobile ? 'flex-start' : 'center', 
           flexWrap: 'wrap', 
           gap: isMobile ? 0.3 : 0.8, 
           flex: 1,
           maxWidth: isMobile ? '240px' : 'none',
           minHeight: isMobile ? '45px' : 'auto',
           alignContent: isMobile ? 'flex-start' : 'center',
           justifyContent: 'flex-start',
           '& > *': {
             flexShrink: 0
           }
         }}>
          {/* 认证类型 */}
          <Chip
            label={getCertificationTypeName(task.certification_type)}
            size="small"
            variant="filled"
            sx={{ 
              borderRadius: isMobile ? '12px' : '20px',
              height: isMobile ? '20px' : '30px', 
              bgcolor: 'rgba(0,0,0,0.04)', 
              color: theme.palette.text.secondary,
              '& .MuiChip-label': { 
                px: isMobile ? 0.8 : 1.5, 
                fontSize: isMobile ? '0.5rem' : '0.7rem',
                fontWeight: 500 
              } 
            }}
          />
            
          {/* 翻译语种 */}
          <Chip
            label={getLanguageName(task.language)}
            size="small"
            variant="filled"
            sx={{ 
              borderRadius: isMobile ? '12px' : '20px',
              height: isMobile ? '20px' : '30px', 
              bgcolor: 'rgba(0,0,0,0.04)', 
              color: theme.palette.text.secondary,
              '& .MuiChip-label': { 
                px: isMobile ? 0.8 : 1.5, 
                fontSize: isMobile ? '0.5rem' : '0.7rem',
                fontWeight: 500 
              } 
            }}
          />
            
          {/* 文件页数 */}
          <Chip
            label={`${task.file_pages} 页`}
            size="small"
            variant="filled"
            sx={{ 
              borderRadius: isMobile ? '12px' : '20px',
              height: isMobile ? '20px' : '30px', 
              bgcolor: 'rgba(0,0,0,0.04)',
              color: theme.palette.text.secondary, 
              '& .MuiChip-label': { 
                px: isMobile ? 0.8 : 1.5, 
                fontSize: isMobile ? '0.5rem' : '0.7rem',
                fontWeight: 500 
              } 
            }}
          />
            
          {/* 消耗点数 */}
          <Chip
            label={task.status === 'cancelled' || task.status === '已取消' 
              ? `消耗${task.points_refunded || task.points_cost} 点` 
              : `消耗${task.points_cost} 点`}
            size="small"
            variant="filled"
            sx={{ 
              borderRadius: isMobile ? '12px' : '20px',
              height: isMobile ? '20px' : '30px', 
              bgcolor: 'rgba(0,0,0,0.04)', 
              color: task.status === 'cancelled' || task.status === '已取消'
                ? theme.palette.text.secondary  
                : theme.palette.text.secondary,
              '& .MuiChip-label': { 
                px: isMobile ? 0.8 : 1.5, 
                fontSize: isMobile ? '0.5rem' : '0.7rem',
                fontWeight: 500 
              } 
            }}
          />
          
          {/* 翻译状态 - 根据状态设置不同的背景色 */}
          <Chip
            label={getStatusName(task.status)}
            size="small"
            variant="filled"
            sx={{ 
              borderRadius: isMobile ? '12px' : '20px',
                height: isMobile ? '20px' : '30px', 
              bgcolor: task.status === 'failed' || task.status === '翻译失败' 
                ? 'rgba(244, 67, 54, 0.15)' 
                : task.status === 'completed' || task.status === '翻译完成' 
                  ? 'rgba(76, 175, 80, 0.15)' 
                  : task.status === 'processing' || task.status === '翻译中'
                    ? 'rgba(255, 193, 7, 0.15)'
                    : task.status === 'cancelled' || task.status === '已取消'
                      ? 'rgba(158, 158, 158, 0.15)'
                      : 'rgba(67, 83, 255, 0.1)', 
              color: task.status === 'failed' || task.status === '翻译失败' 
                ? 'error.main' 
                : task.status === 'completed' || task.status === '翻译完成'
                  ? '#2e7d32'
                  : task.status === 'processing' || task.status === '翻译中'
                    ? '#ed6c02'
                    : task.status === 'cancelled' || task.status === '已取消'
                      ? 'text.secondary'
                      : 'primary.main',
              '& .MuiChip-label': { 
                px: isMobile ? 0.8 : 1.5, 
                fontSize: isMobile ? '0.5rem' : '0.7rem',
                fontWeight: 500
              } 
            }}
          />

          {/* 加急标签 - 仅在任务是加急的时候显示 */}
          {task.is_urgent && (
            <Chip
              label="加急"
              size="small"
              variant="filled"
              sx={{ 
                borderRadius: isMobile ? '12px' : '20px',
                  height: isMobile ? '20px' : '30px', 
                bgcolor: 'rgba(255, 87, 34, 0.15)', 
                color: '#ff5722',
                '& .MuiChip-label': { 
                  px: isMobile ? 0.8 : 1.5, 
                  fontSize: isMobile ? '0.5rem' : '0.7rem',
                  fontWeight: 500
                } 
              }}
            />
          )}

          {/* 文件类型 - 移到翻译状态后面，当文件类型为空或未识别时不显示 */}
          {task.file_type && task.file_type !== '未识别' && (
            <Chip
              label={task.file_type}
              size="small"
              variant="filled"
              sx={{ 
                borderRadius: isMobile ? '12px' : '20px',
                  height: isMobile ? '20px' : '30px', 
                bgcolor: 'rgba(0,0,0,0.02)', 
                '& .MuiChip-label': { 
                  px: isMobile ? 0.8 : 1.5, 
                  fontSize: isMobile ? '0.5rem' : '0.7rem',
                  fontWeight: 500,
                  color: theme.palette.text.secondary
                } 
              }}
            />
          )}
            
          {/* 文件ID */}
          <Typography 
            variant="caption" 
            color="text.secondary" 
            sx={{ 
              fontSize: isMobile ? '0.5rem' : '0.7rem',
              bgcolor: 'rgba(0,0,0,0.02)',
              px: isMobile ? 0.6 : 1,
              py: isMobile ? 0.15 : 0.25,
              borderRadius: isMobile ? '6px' : '10px'
            }}
          >
             {task.task_id}
          </Typography>
            
          {/* 剩余有效期 - 仅在任务完成时显示 */}
          {(task.status === 'completed' || task.status === '翻译完成') && formatRemainingTime() && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Typography 
                variant="caption" 
                color="text.secondary" 
                sx={{ 
                  fontSize: isMobile ? '0.5rem' : '0.7rem',
                  bgcolor: 'rgba(0,0,0,0.02)',
                  px: isMobile ? 0.6 : 1,
                  py: isMobile ? 0.15 : 0.25,
                  borderRadius: isMobile ? '6px' : '10px'
                }}
              >
                <span style={{ 
                  color: isFileExpired() ? theme.palette.error.main : theme.palette.warning.main,
                  fontWeight: 500
                }}>
                  {formatRemainingTime()}
                </span>
              </Typography>
            </Box>
          )}
        </Box>
            
        {/* 操作按钮 - 右侧 */}
        <Box sx={{ 
          display: 'flex', 
          gap: isMobile ? 0.3 : 1,
          mr: isMobile ? 0.8 : 2,
          ml: 1,
          minWidth: isMobile ? '80px' : '130px',
          justifyContent: 'flex-end'
        }}>
          {/* 预测剩余时间 - 显示在操作按钮最右侧 */}
          {(task.status === 'waiting' || task.status === '待翻译' || 
            task.status === 'processing' || task.status === '翻译中') && 
           task.prediction && task.prediction.estimated_time_remaining && (
            <Typography 
              variant="caption" 
              sx={{ 
                fontSize: isMobile ? '0.6rem' : '0.9rem',
                fontWeight: 500,
                color: theme.palette.primary.main,
                alignSelf: 'center',
                whiteSpace: 'nowrap',
                mr: isMobile ? 1.5 : 4
              }}
            >
              {task.prediction.estimated_time_remaining}
            </Typography>
          )}

          {/* 下载按钮 - 仅在文件未过期且任务完成时显示 */}
          {canDownload && (
            <Box sx={{ display: 'flex', gap: isMobile ? 0.3 : 0.5 }}>
              <Button
                startIcon={!isMobile ? <GetAppIcon sx={{ fontSize: '1.2rem' }} /> : undefined}
                size="small"
                variant="contained"
                color="primary"
                onClick={() => downloadFile(task.task_id)}
                sx={{ 
                  borderRadius: isMobile ? '50px' : '60px', 
                  py: 0, 
                  width: isMobile ? '60px' : '100px', 
                  height: isMobile ? '30px' : '36px', 
                  fontSize: isMobile ? '0.6rem' : '0.8rem',
                  boxShadow: '0 4px 10px rgba(67, 83, 255, 0.2)',
                  '&:hover': {
                    boxShadow: '0 6px 15px rgba(67, 83, 255, 0.3)',
                    transform: 'translateY(-2px)'
                  },
                  transition: 'all 0.2s ease'
                }}
              >
                下载
              </Button>
              <Button
                startIcon={!isMobile ? <DeleteIcon sx={{ fontSize: '1.2rem' }} /> : undefined}
                size="small"
                variant="outlined"
                color="error"
                onClick={handleDeleteFiles}
                disabled={loading}
                sx={{ 
                  borderRadius: isMobile ? '50px' : '60px', 
                  py: 0, 
                  width: isMobile ? '60px' : '100px', 
                  height: isMobile ? '30px' : '36px', 
                  fontSize: isMobile ? '0.6rem' : '0.8rem',
                  borderColor: theme.palette.error.main,
                  color: theme.palette.error.main,
                  '&:hover': {
                    backgroundColor: theme.palette.error.main,
                    color: 'white',
                    transform: 'translateY(-2px)'
                  },
                  transition: 'all 0.2s ease'
                }}
              >
                删除
              </Button>
            </Box>
          )}

          {/* 文件已清理提示 - 在文件过期时显示 */}
          {showFileCleanedTip && (
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: 0.5,
              px: 1.5,
              py: 0.5,
              borderRadius: '60px',
              color: '#CCCCCC'
            }}>
              <Typography 
                variant="caption" 
                sx={{ 
                  fontSize: isMobile ? '0.6rem' : '0.8rem',
                  color: '#CCCCCC'
                }}
              >
                已安全删除
              </Typography>
              <Tooltip 
                title="为保护用户隐私，文件已彻底删除" 
                arrow 
                placement="top"
              >
                <InfoIcon 
                  sx={{ 
                    fontSize: isMobile ? '0.7rem' : '1rem', 
                    color: '#CCCCCC',
                    cursor: 'help'
                  }} 
                />
              </Tooltip>
            </Box>
          )}
        </Box>
      </Card>
      
      {/* 删除确认对话框 */}
      <DeleteConfirmDialog
        open={deleteDialogOpen}
        onClose={handleCancelDelete}
        onConfirm={handleConfirmDelete}
        fileName={displayFileName}
        loading={loading}
      />
    </>
  );
};

export default TaskCard;